import { useEffect, useRef } from 'react';

import {
  FinalcutProgressData,
  FileProgressData,
  subscribeFcRenderServer,
  subscribeFileProgressServer,
  pingFileProgressServer,
  pingFcRenderServer,
  FileQueueData,
  FinalcutQueueData,
} from 'services/sse.service';
import { useAppSelector, useAppDispatch } from 'store/store-hooks';
import { isFcStatusCompleted, isFilesProcessing } from 'components/app/_helpers/_app.helper';
import {
  anyProcessingFinalCuts,
  handleProcessingFc,
  setFinalcutQueue,
} from 'components/app/finalcuts/_mediator/fc-render-server.slice';
import { useSse } from 'components/app/_hooks/use-sse';
import { anyProcessingFiles, handleProcessingFiles, setFileQueue } from '../app-files-upload.slice';
import { FileStatus } from 'services/file.service';
import { LogLevel } from 'services/logger.service';
import { appLogger } from '../app-logger.slice';

export const useAppSse = () => {
  const finalcuts = useAppSelector((state) => state.app__data.finalcuts);
  const fileStatuses = useAppSelector((state) => state.app__files.statuses);
  const uploadingFiles = useAppSelector((state) => state.app__filesUpload.uploadingFiles);
  const dispatch = useAppDispatch();
  const prevLen = useRef(0);

  const sseFc = useSse<FinalcutProgressData, FinalcutQueueData>({
    pingCb: pingFcRenderServer,
    subscribe: subscribeFcRenderServer,
    onProgress: async (data) => await dispatch(handleProcessingFc(data)).unwrap(),
    onQueue: (data) => dispatch(setFinalcutQueue(data)),
    onError: async (error) => {
      const processingFinalCuts = finalcuts.filter((fc) => !isFcStatusCompleted(fc.status));

      if (processingFinalCuts.length) {
        const errorData = {
          error: error,
          finalcuts: finalcuts,
          processingFinalCutsCount: processingFinalCuts.length,
        };
        const message = `SSE FinalCut connection error: ${error} | Data: ${JSON.stringify(errorData)}`;
        dispatch(appLogger({ level: LogLevel.Fatal, error: { name: 'SSE FinalCut', message } }));

        // Check if there are any processing final cuts and restart SSE if needed
        const resFinalCuts = await dispatch(anyProcessingFinalCuts()).unwrap();
        if (resFinalCuts) {
          const restartMessage = `SSE FinalCut: Restarting SSE connection - found ${resFinalCuts} processing final cuts on server`;
          dispatch(
            appLogger({ level: LogLevel.Info, error: { name: 'SSE FinalCut Restart', message: restartMessage } })
          );
          sseFc.listen();
        }
      }
    },
  });

  const sseFiles = useSse<FileProgressData, FileQueueData>({
    pingCb: pingFileProgressServer,
    subscribe: subscribeFileProgressServer,
    onProgress: async (data) => await dispatch(handleProcessingFiles(data)).unwrap(),
    onQueue: (data) => dispatch(setFileQueue(data)),
    onError: async (error) => {
      const processingFiles = Object.entries(fileStatuses).filter(
        ([_, status]) => status === FileStatus.Processing || status === FileStatus.PendingProcessing
      );

      if (processingFiles.length) {
        const errorData = {
          error: error,
          fileStatuses: fileStatuses,
          processingFilesCount: processingFiles.length,
        };
        const message = `SSE Files connection error: ${error} | Data: ${JSON.stringify(errorData)}`;
        dispatch(appLogger({ level: LogLevel.Fatal, error: { name: 'SSE Files', message } }));

        // Check if there are any processing files and restart SSE if needed
        const resFiles = await dispatch(anyProcessingFiles()).unwrap();
        if (resFiles) {
          const restartMessage = `SSE Files: Restarting SSE connection - found ${resFiles} processing files on server`;
          dispatch(appLogger({ level: LogLevel.Info, error: { name: 'SSE Files Restart', message: restartMessage } }));
          sseFiles.listen();
        }
      }
    },
  });

  const checkProcessing = async () => {
    const resFiles = dispatch(anyProcessingFiles()).unwrap();
    const resFinalCuts = dispatch(anyProcessingFinalCuts()).unwrap();
    const [isFiles, isFinalCuts] = await Promise.all([resFiles, resFinalCuts]);
    if (isFinalCuts) sseFc.listen();
    if (isFiles) sseFiles.listen();
  };

  useEffect(() => {
    checkProcessing();
  }, []);

  useEffect(() => {
    if (finalcuts.length === 0) return;
    if (finalcuts.some((el) => !isFcStatusCompleted(el.status))) sseFc.listen();
  }, [finalcuts]);

  useEffect(() => {
    if (isFilesProcessing(fileStatuses)) sseFiles.listen();
  }, [fileStatuses]);

  useEffect(() => {
    if (prevLen.current > uploadingFiles.length) sseFiles.listen();
    prevLen.current = uploadingFiles.length;
  }, [uploadingFiles.length]);
};
